import React from 'react';
import { Printer, X } from 'lucide-react';
import QRCodeGenerator from './QRCodeGenerator';
import { dateTimeFormatter } from './DateTimeFormatter';
import './PatientStickerLabel.css';

/**
 * Reusable Patient Sticker Label Modal component
 * Renders an ultra-compact 3.5" x 2" printable barcode/QR sticker label for patient record files.
 */
const PatientStickerLabel = ({ isOpen, onClose, patient }) => {
  if (!isOpen || !patient) return null;

  const isIP = Boolean(patient.admissionId || patient.patientType === 'IN_PATIENT');

  return (
    <div className="modern-modal-overlay" onClick={onClose}>
      <div className="modern-modal-card animated-modal sticker-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="modal-top-accent-bar" />
        <div className="modern-modal-header">
          <div className="modal-title-container">
            <div className="modal-icon-badge blue-glow">
              <Printer size={20} className="icon-blue" />
            </div>
            <div>
              <h3 className="modal-main-heading">{isIP ? "IP Admission Sticker Label" : "Patient File Label Sticker"}</h3>
              <p className="modal-sub-heading">Compact 3.5" x 2" printable barcode/QR sticker for patient record file</p>
            </div>
          </div>
          <button className="modal-close-icon-btn" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <div className="modern-modal-body sticker-preview-body">
          {/* Physical Printable Sticker Card */}
          <div id="sticker-label-printable" className="sticker-label-container">
            <div className="sticker-header">
              <span className="sticker-hospital-name">VT HOSPITALS</span>
              <span className="sticker-type-tag">{isIP ? "INPATIENT" : "HINDUPUR"}</span>
            </div>

            <div className="sticker-content-grid">
              <div className="sticker-info-col">
                {isIP ? (
                  <>
                    <div className="sticker-field">
                      <span className="field-lbl">MR NO:</span>
                      <span className="field-val font-mono mr-highlight">{patient.mrNumber || 'N/A'}</span>
                    </div>
                    <div className="sticker-field">
                      <span className="field-lbl">IP NO:</span>
                      <span className="field-val font-mono mr-highlight">{patient.admissionId || 'N/A'}</span>
                    </div>
                    <div className="sticker-field">
                      <span className="field-lbl">NAME:</span>
                      <span className="field-val name-val">{patient.patientName || patient.name || 'N/A'}</span>
                    </div>
                    <div className="sticker-field">
                      <span className="field-lbl">AGE/GENDER:</span>
                      <span className="field-val">{patient.age || 'N/A'}Y / {patient.gender || 'N/A'}</span>
                    </div>
                    <div className="sticker-field">
                      <span className="field-lbl">PHONE NUMBER:</span>
                      <span className="field-val">
                        {patient.phoneNumber || patient.phone || patient.contactNumber || patient.mobileNumber || 'N/A'}
                      </span>
                    </div>
                    <div className="sticker-field">
                      <span className="field-lbl">ROOM:</span>
                      <span className="field-val">
                        Bed {patient.bedNumber || 'N/A'} FLOOR {patient.floor || patient.floorNumber || 'N/A'}
                      </span>
                    </div>
                    <div className="sticker-field">
                      <span className="field-lbl">DOA/TOA:</span>
                      <span className="field-val">{dateTimeFormatter(patient.admissionDate, patient.admissionTime)}</span>
                    </div>
                    <div className="sticker-field">
                      <span className="field-lbl">DOCTOR:</span>
                      <span className="field-val">{patient.doctorName || patient.doctor?.doctorName || patient.doctorId?.doctorName || 'N/A'}</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="sticker-field">
                      <span className="field-lbl">MR NO:</span>
                      <span className="field-val font-mono mr-highlight">{patient.mrNumber}</span>
                    </div>
                    <div className="sticker-field">
                      <span className="field-lbl">NAME:</span>
                      <span className="field-val name-val">{patient.patientName || patient.name}</span>
                    </div>
                    <div className="sticker-field-row">
                      <div className="sticker-field">
                        <span className="field-lbl">AGE/SEX:</span>
                        <span className="field-val">{patient.age || 'N/A'}Y / {patient.gender || 'N/A'}</span>
                      </div>
                      <div className="sticker-field">
                        <span className="field-lbl">TOKEN:</span>
                        <span className="field-val font-mono token-val">#{patient.tokenNumber || '1'}</span>
                      </div>
                    </div>
                    <div className="sticker-field">
                      <span className="field-lbl">PHONE:</span>
                      <span className="field-val">
                        {patient.phoneNumber || patient.phone || patient.contactNumber || patient.mobileNumber || 'N/A'}
                      </span>
                    </div>
                    <div className="sticker-field">
                      <span className="field-lbl">DATE/TIME:</span>
                      <span className="field-val">{dateTimeFormatter(patient.appointmentDate, patient.timeSlot)}</span>
                    </div>
                    <div className="sticker-field">
                      <span className="field-lbl">ADDRESS:</span>
                      <span className="field-val address-val">{patient.address || 'N/A'}</span>
                    </div>
                  </>
                )}
              </div>

              <div className="sticker-qr-col">
                <QRCodeGenerator value={isIP ? (patient.admissionId || patient.mrNumber) : patient.mrNumber} size={76} caption={isIP ? (patient.admissionId || patient.mrNumber) : patient.mrNumber} />
              </div>
            </div>
          </div>
        </div>

        <div className="modern-modal-footer">
          <button className="modern-btn-secondary" onClick={onClose}>
            Close
          </button>
          <button className="modern-btn-primary" onClick={() => window.print()}>
            <Printer size={16} />
            <span>Print Label Sticker</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PatientStickerLabel;
